import { useCallback, useEffect, useRef, useState } from "react";

interface Draft {
  text: string;
  replyToMessageId?: string;
  replyToSenderName?: string;
  replyToText?: string;
}

const STORAGE_KEY = "scrile_msg_drafts";

function loadFromStorage(): Map<string, Draft> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return new Map(JSON.parse(raw) as [string, Draft][]);
  } catch (_e) {
    /* localStorage unavailable */
  }
  return new Map();
}

function persistToStorage(map: Map<string, Draft>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...map.entries()]));
  } catch (_e) {
    /* localStorage unavailable */
  }
}

const draftsMap = loadFromStorage();

// Module-level pub/sub. `useDraftText(threadId)` uses setState equality to bail
// out of notifications that don't affect its specific thread, so a typing storm
// in one chat doesn't re-render every row of the sidebar list.
const listeners = new Set<() => void>();
function notify(): void {
  listeners.forEach((l) => l());
}

/**
 * Clears all drafts (in-memory + localStorage). Call this on user logout to prevent
 * draft content leaking into a subsequent user's session.
 */
export function clearAllDrafts(): void {
  draftsMap.clear();
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (_e) {
    /* localStorage unavailable */
  }
  notify();
}

/**
 * Module-level draft write for non-hook contexts. Used by the send queue's
 * abort path: a queued send interrupted by navigation stashes its captured
 * text as the ORIGIN thread's draft instead of silently losing it. Never
 * clobbers a non-empty draft the user already has for that thread.
 */
export function saveDraftText(threadId: string, text: string): void {
  if (!threadId || !text) return;
  const prev = draftsMap.get(threadId);
  if (prev && prev.text) return;
  draftsMap.set(threadId, { ...(prev || {}), text });
  persistToStorage(draftsMap);
  notify();
}

export default function useDrafts() {
  const mapRef = useRef(draftsMap);
  const [, setVersion] = useState(0);

  useEffect(() => {
    const listener = () => setVersion((v) => v + 1);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  const saveDraft = useCallback((threadId: string, draft: Draft) => {
    const prev = mapRef.current.get(threadId);
    if (!draft.text && !draft.replyToMessageId) {
      if (!prev) return;
      mapRef.current.delete(threadId);
    } else {
      if (prev && prev.text === draft.text && prev.replyToMessageId === draft.replyToMessageId) return;
      mapRef.current.set(threadId, draft);
    }
    persistToStorage(mapRef.current);
    notify();
  }, []);

  const getDraft = useCallback((threadId: string): Draft | undefined => {
    return mapRef.current.get(threadId);
  }, []);

  const clearDraft = useCallback((threadId: string) => {
    if (!mapRef.current.has(threadId)) return;
    mapRef.current.delete(threadId);
    persistToStorage(mapRef.current);
    notify();
  }, []);

  const hasDraft = useCallback((threadId: string): boolean => {
    const draft = mapRef.current.get(threadId);
    return !!(draft && draft.text);
  }, []);

  const getDraftText = useCallback((threadId: string): string | undefined => {
    return mapRef.current.get(threadId)?.text;
  }, []);

  return { saveDraft, getDraft, clearDraft, hasDraft, getDraftText };
}

/**
 * Subscribes to drafts changes for a single thread. Narrower than `useDrafts` —
 * returns only the draft text and relies on `setState` identity bailout so
 * MessageThreadItem rows with unchanged drafts don't re-render while the user
 * types in some other chat.
 */
export function useDraftText(threadId: string | undefined): string {
  const [text, setText] = useState<string>(() => (threadId ? draftsMap.get(threadId)?.text || "" : ""));
  useEffect(() => {
    if (!threadId) {
      setText("");
      return;
    }
    const compute = () => draftsMap.get(threadId)?.text || "";
    setText(compute());
    const listener = () => setText(compute());
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, [threadId]);
  return text;
}
